import { SelectOption } from '@/lib/Selects/Select/types';
import { PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment, FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { DATE_RANGE_CUSTOM_ID, getFilterOptionDisplayLabel, toFilterMenuOptions } from '../../utils/filterOptions';
import { DateRangeCustomPanel } from '../DateRangeCustomPanel';
import { FilterDisabledTooltip, FilterDisabledTooltipSource } from '../FilterDisabledTooltip';
import { paChipClass, paChipDisabledClass, paChipOpenClass, paPopClass, paPopNodeClass, paPopNodeSelectedClass } from '../PerformanceAudienceComponents';

type FilterPillSelectProps = {
  icon: ReactNode;
  categoryLabel?: string;
  selected: PerformanceAudienceFilterOption | null;
  items: PerformanceAudienceFilterOption[];
  onChange: (option: SelectOption) => void;
  variant?: 'labeled' | 'date';
  disabled?: boolean;
  disabledTooltipSource?: Extract<FilterDisabledTooltipSource, 'google' | 'meta'>;
  className?: string;
  accountCountSuffix?: string;
  customRangeLabel?: string;
  customDateFrom?: string;
  customDateTo?: string;
  onCustomDateRangeApply?: (range: { from: string; to: string }) => void;
};

const dateItemClass = 'flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] text-ink-700 hover:bg-ink-50';

const dateItemOnClass = 'bg-ar-blue-soft font-semibold text-ar-blue-dark';

export const FilterPillSelect: FC<FilterPillSelectProps> = ({
  icon,
  categoryLabel,
  selected,
  items,
  onChange,
  variant = 'labeled',
  disabled,
  disabledTooltipSource = 'google',
  className,
  accountCountSuffix,
  customRangeLabel,
  customDateFrom,
  customDateTo,
  onCustomDateRangeApply,
}) => {
  const { t } = useTranslation('performanceAudience');
  const rootRef = useRef<HTMLDivElement>(null);
  const [showCustomPanel, setShowCustomPanel] = useState(false);

  const menuItems = toFilterMenuOptions(items, t);
  const presetMenuItems = menuItems.filter((item) => item.id !== DATE_RANGE_CUSTOM_ID);
  const customMenuItem = menuItems.find((item) => item.id === DATE_RANGE_CUSTOM_ID);
  const selectedMenuItem = menuItems.find((item) => item.id === selected?.id) ?? null;
  const displayLabel = selected ? getFilterOptionDisplayLabel(selected, t, customRangeLabel) : '';

  useEffect(() => {
    if (selected?.id === DATE_RANGE_CUSTOM_ID && customDateFrom && customDateTo) {
      return;
    }
    if (selected?.id !== DATE_RANGE_CUSTOM_ID) {
      setShowCustomPanel(false);
    }
  }, [selected?.id, customDateFrom, customDateTo]);

  const closeMenu = () => {
    const button = rootRef.current?.querySelector<HTMLButtonElement>('button');
    button?.click();
  };

  const handlePresetChange = (option: SelectOption) => {
    setShowCustomPanel(false);
    onChange(option);
  };

  const handleCustomOpen = () => {
    setShowCustomPanel(true);
  };

  const handleCustomApply = (range: { from: string; to: string }) => {
    onCustomDateRangeApply?.(range);
    setShowCustomPanel(false);
    closeMenu();
  };

  const handleCustomCancel = () => {
    setShowCustomPanel(false);
    closeMenu();
  };

  return (
    <FilterDisabledTooltip disabled={Boolean(disabled)} source={disabledTooltipSource}>
      <Listbox value={selectedMenuItem} onChange={handlePresetChange} disabled={disabled} by={(a, b) => a?.id === b?.id}>
      {({ open }) => (
        <div
          ref={rootRef}
          className={twMerge(paChipClass, open && paChipOpenClass, disabled && paChipDisabledClass, variant === 'date' && 'ml-auto max-sm:ml-0', className)}
        >
          <Listbox.Button className='flex w-full items-center gap-2 border-0 bg-transparent p-0 text-left outline-none'>
            <span className='shrink-0'>{icon}</span>
            {variant === 'labeled' && categoryLabel ? <span className='text-ink-500'>{categoryLabel}</span> : null}
            <span className='truncate font-semibold text-ink-900'>{displayLabel}</span>
            {accountCountSuffix ? <span className='text-[11.5px] font-normal text-ink-400'>{accountCountSuffix}</span> : null}
            <svg
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden
            >
              <path d='M6 9l6 6 6-6' />
            </svg>
          </Listbox.Button>
          <Transition
            show={open}
            as={Fragment}
            enter='transition ease-in duration-100'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition ease-in duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={() => setShowCustomPanel(false)}
          >
            <Listbox.Options
              static
              className={twMerge(
                paPopClass,
                variant === 'date' && 'right-0 left-auto flex w-[220px] items-stretch gap-0 p-1.5',
                variant === 'date' && showCustomPanel && 'w-auto',
                variant !== 'date' && 'min-w-[280px] w-80'
              )}
            >
              {variant === 'date' ? (
                <>
                  <div className={twMerge('w-[208px] shrink-0', showCustomPanel && 'mr-1.5 border-r border-ink-100 pr-1.5')}>
                    {presetMenuItems.map((item, index) => (
                      <Fragment key={item.id}>
                        {index === 5 ? <div className='mx-1.5 my-1 h-px bg-ink-100' /> : null}
                        <Listbox.Option value={item} disabled={item.disabled} className='outline-none'>
                          {({ selected: isSelected, disabled: isDisabled }) => (
                            <div className={twMerge(dateItemClass, isSelected && dateItemOnClass, isDisabled && 'cursor-not-allowed opacity-55')}>
                              <span>{item.label}</span>
                              {isSelected ? <span className='text-[11px]'>✓</span> : null}
                            </div>
                          )}
                        </Listbox.Option>
                      </Fragment>
                    ))}
                    {customMenuItem ? (
                      <>
                        <div className='mx-1.5 my-1 h-px bg-ink-100' />
                        <button
                          type='button'
                          className={twMerge(dateItemClass, 'w-full', (showCustomPanel || selected?.id === DATE_RANGE_CUSTOM_ID) && dateItemOnClass)}
                          onClick={handleCustomOpen}
                        >
                          <span>{customMenuItem.label}</span>
                          <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden>
                            <path d='M9 6l6 6-6 6' />
                          </svg>
                        </button>
                      </>
                    ) : null}
                  </div>
                  {showCustomPanel ? (
                    <DateRangeCustomPanel initialFrom={customDateFrom} initialTo={customDateTo} onApply={handleCustomApply} onCancel={handleCustomCancel} />
                  ) : null}
                </>
              ) : (
                <div className='max-h-[380px] overflow-y-auto py-1'>
                  {menuItems.map((item) => (
                    <Listbox.Option key={item.id} value={item} disabled={item.disabled} className='outline-none'>
                      {({ selected: isSelected }) => (
                        <div className={twMerge(paPopNodeClass, isSelected && paPopNodeSelectedClass)}>
                          <div className='min-w-0 flex-1'>
                            <div className={twMerge('font-medium text-ink-900', isSelected && 'font-semibold text-ar-blue-dark')}>{item.label}</div>
                          </div>
                          {isSelected ? <span className='font-bold text-ar-blue-dark'>✓</span> : null}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </div>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
    </FilterDisabledTooltip>
  );
};
